import { useCallback, useMemo, useState } from 'react'
import { IconCirclePlus, IconPlus, IconTrash } from '@tabler/icons-react'

import { ScalrBreadcrumb } from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table/DataTable'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'

import { KPIGoal, saveKPIGoals } from '@/api/kpi'
import { useKpiGoals } from '@/hooks/kpi'
import { Branch, Region } from '@/types'
import { KPIGoalPlanTableModel } from '@/types/targets'
import { kpiGoalPlanTableColumns } from '@/utils'
import KPITable from '@/screens/targets/KPITable'

interface Selectable {
  id: string
  name: string
}

const REGIONS_MAP = JSON.parse(
  sessionStorage.getItem('regions') ?? '[]'
) as Region[]

const ALL_BRANCHES = REGIONS_MAP.reduce((acc: Branch[], region: Region) => {
  return [...acc, ...region.branches]
}, [])

const QUARTERS = [
  { id: '-1', name: 'No selection' },
  { id: '1', name: 'Q1' },
  { id: '2', name: 'Q2' },
  { id: '3', name: 'Q3' },
  { id: '4', name: 'Q4' },
]

const YEARS = [
  { id: '-1', name: 'No selection' },
  ...Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => ({
    id: (2024 + i).toString(),
    name: (2024 + i).toString(),
  })),
]

const getSelectableQuarters = () => {
  const quarters = []

  // Add all quarters for 2023, 2024, 2025
  for (const year of [2023, 2024, 2025]) {
    for (let quarter = 1; quarter <= 4; quarter++) {
      const rowName = `Q${quarter} ${year}`
      quarters.push({ id: rowName, name: rowName })
    }
  }

  return quarters
}

const Selection = ({
  name,
  options,
  selected,
  setSelected,
}: {
  name: string
  options: Selectable[]
  selected: Selectable | undefined
  setSelected: (value: string) => void
}) => (
  <Select value={selected?.id} onValueChange={(value) => setSelected(value)}>
    <SelectTrigger className="w-[10rem]">
      <SelectValue placeholder={`Select a ${name}`} />
    </SelectTrigger>
    <SelectContent>
      {options.map((opt: Selectable) => (
        <SelectItem key={opt.id} value={opt.id ?? '-1'}>
          {opt.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
)

type ListSelections = {
  branch?: Selectable
  year?: Selectable
  quarter?: Selectable
}

type AdditionSelections = {
  branch?: Selectable
  year?: Selectable
  quarter?: Selectable
  kpi?: Selectable
}

const KPIGoalPlanPage = () => {
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [listSelections, setListSelections] = useState<ListSelections>({
    branch: undefined,
    year: undefined,
    quarter: undefined,
  })
  const [additionSelections, setAdditionSelections] =
    useState<AdditionSelections>({
      branch: undefined,
      year: undefined,
      quarter: undefined,
      kpi: undefined,
    })
  const [targetValue, setTargetValue] = useState(0)
  const [kpiGoals, setKpiGoals] = useState<KPIGoal[]>([])
  const selectableQuarters = getSelectableQuarters()
  const { options, oldKPIGoals, deleteKPIGoal, loading, fetchKPIGoals } =
    useKpiGoals(listSelections)

  const handleAddKPI = useCallback(() => {
    const selectedBranch = additionSelections.branch
    const selectedKPI = additionSelections.kpi
    if (!selectedBranch || !selectedKPI) {
      return
    }
    const newKPI: KPIGoal = {
      id: kpiGoals.length + 1,
      branch: selectedBranch.id,
      year: additionSelections.year?.id ?? '2025',
      quarter: additionSelections.quarter?.id ?? '1',
      milestones: [targetValue],
      kpi: selectedKPI.id,
    }
    setKpiGoals((prev) => [...prev, newKPI])
  }, [kpiGoals, additionSelections, targetValue, setKpiGoals])

  const handleRemoveKPI = useCallback(
    (id: number | string) => {
      setKpiGoals((prev) => prev.filter((kpi) => kpi.id !== id))
    },
    [setKpiGoals]
  )

  const handleSheetClose = () => {
    const selectedBranch = additionSelections.branch
    const selectedKPI = additionSelections.kpi
    if (
      selectedBranch ||
      selectedKPI ||
      kpiGoals.length > 0 ||
      targetValue > 0
    ) {
      const confirmClose = window.confirm(
        'Are you sure you want to close without saving?'
      )
      if (confirmClose) {
        handleSheetOpen(false)
      }
    } else {
      handleSheetOpen(false)
    }
  }

  const handleSaveKPIGoals = async () => {
    if (kpiGoals.length === 0) {
      alert('Please add at least one KPI goal')
      return
    }
    const selectedQuarter = additionSelections.quarter
    if (!selectedQuarter) {
      alert('Please select a quarter')
      return
    }
    const goals = kpiGoals.map((goal) => ({
      ...goal,
      year: goal.year,
      quarter: selectedQuarter.id,
    }))
    try {
      await saveKPIGoals(goals).then(() => {
        setIsSheetOpen(false)
      })
    } catch (error) {
      console.error('Error saving KPI goals:', error)
      return
    }
    setKpiGoals([])
    setAdditionSelections({
      branch: undefined,
      year: undefined,
      quarter: undefined,
    })
    setListSelections({
      branch: undefined,
      year: undefined,
      quarter: undefined,
    })
    setTargetValue(0)
    setIsSheetOpen(false)
    fetchKPIGoals()
  }

  const handleSheetOpen = (val: boolean | undefined = undefined) => {
    if (val !== undefined) {
      setIsSheetOpen(val)
    } else {
      setIsSheetOpen((prev) => !prev)
    }
    if (isSheetOpen) {
      setKpiGoals([])
      setAdditionSelections({
        branch: undefined,
        year: undefined,
        quarter: undefined,
      })
      setListSelections({
        branch: undefined,
        year: undefined,
        quarter: undefined,
      })
      setTargetValue(0)
    }
  }

  const handleDeleteKPI = async (id: string | number) => {
    if (typeof id !== 'string') return
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this KPI?'
    )
    if (confirmDelete) {
      await deleteKPIGoal(id.toString())
    }
  }

  const handleListSelection = (value: string, key: keyof ListSelections) => {
    switch (key) {
      case 'branch':
        const branch = ALL_BRANCHES.find((branch) => branch.id === value)
        setListSelections({ ...listSelections, branch })
        break
      case 'year':
        const year = YEARS.find((year) => year.id === value)
        setListSelections({ ...listSelections, year })
        break
      case 'quarter':
        const quarter = QUARTERS.find((quarter) => quarter.id === value)
        setListSelections({ ...listSelections, quarter })
        break
    }
  }

  const handleAdditionSelection = (
    value: string,
    key: keyof AdditionSelections
  ) => {
    switch (key) {
      case 'branch':
        const branch = ALL_BRANCHES.find((branch) => branch.id === value)
        setAdditionSelections({ ...additionSelections, branch })
        break
      case 'quarter':
        const [selectedQ, selectedY] = value.split(' ')
        const quarter = QUARTERS.find((quarter) => quarter.name === selectedQ)
        const year = YEARS.find((year) => year.name === selectedY)
        setAdditionSelections({ ...additionSelections, quarter, year })
        break
      case 'kpi':
        const kpi = options.find((opt) => opt.id === value)
        setAdditionSelections({ ...additionSelections, kpi })
        break
    }
  }

  const goalsData: KPIGoalPlanTableModel[] = useMemo(
    () =>
      oldKPIGoals.map((kpi) => {
        const kpiObj = options.find((opt) => opt.id === kpi.kpi) || {
          id: '',
          target_fields: [],
          target_models: [],
          calculation: '',
          description: '',
          title: '',
          area: '',
        }
        const branch = ALL_BRANCHES.find((b) => b.id === kpi.branch) || {
          id: 0,
          name: '',
          region: 0,
        }
        return {
          kpiId: kpi.kpi,
          area: kpiObj.area,
          branch: branch.name,
          kpiName: kpiObj.title,
          calculation: kpiObj.calculation,
          description: kpiObj.description,
          target: kpi.milestones[0],
          bonus: 0,
          achievement: [0, 0, 0, 0],
          action: (
            <Button
              variant="ghost"
              onClick={() => handleDeleteKPI(kpi.id)}
              size="sm"
              className="h-8 w-8 p-0 bg-white border-none"
            >
              <IconTrash className="text-red-500" />
            </Button>
          ),
        }
      }),
    [oldKPIGoals, options]
  )

  return (
    <Card className="w-full min-h-screen">
      <CardHeader>
        <CardTitle>KPI Goal Plan</CardTitle>
      </CardHeader>
      <CardContent>
        <ScalrBreadcrumb
          breadcrumbItems={[{ label: 'Targets', href: '/targets' }]}
          title="KPI Goal Plan"
        />
        {/* main page */}
        <div className="flex justify-between items-center my-6">
          <div className="flex space-x-4">
            <Selection
              name="Year"
              options={YEARS}
              selected={listSelections.year}
              setSelected={(value) => handleListSelection(value, 'year')}
            />
            <Selection
              name="Quarter"
              options={QUARTERS}
              selected={listSelections.quarter}
              setSelected={(value) => handleListSelection(value, 'quarter')}
            />
            <Selection
              name="Branch"
              options={ALL_BRANCHES}
              selected={listSelections.branch}
              setSelected={(value) => handleListSelection(value, 'branch')}
            />
          </div>
          {/* TODO: change the create screen */}
          <Sheet open={isSheetOpen} onOpenChange={handleSheetOpen}>
            <SheetTrigger asChild className="w-fit">
              <Button variant="default">
                <IconPlus className="mr-2" />
                <span className="text-sm">Add KPI Goal</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="top" className="h-[100%] space-y-5">
              <SheetHeader>
                <SheetTitle>Create Goal Plan</SheetTitle>
              </SheetHeader>
              <div className="flex justify-between space-x-4">
                <div className="flex space-x-4">
                  <Selection
                    name="Branch"
                    options={ALL_BRANCHES}
                    selected={additionSelections.branch}
                    setSelected={(value) =>
                      handleAdditionSelection(value, 'branch')
                    }
                  />
                  <Selection
                    name="KPI"
                    options={options}
                    selected={additionSelections.kpi}
                    setSelected={(value) =>
                      handleAdditionSelection(value, 'kpi')
                    }
                  />
                  <Selection
                    name="Quarter"
                    options={selectableQuarters}
                    selected={selectableQuarters.find(
                      (q) =>
                        q.id ===
                        `Q${additionSelections.quarter?.id} ${additionSelections.year?.id}`
                    )}
                    setSelected={(value) =>
                      handleAdditionSelection(value, 'quarter')
                    }
                  />
                  <Input
                    placeholder="Target Value"
                    type="number"
                    min="0"
                    step="any"
                    value={targetValue}
                    onChange={(e) => setTargetValue(Number(e.target.value))}
                    className="w-[10rem]"
                  />
                </div>

                <Button onClick={handleAddKPI}>
                  <span className="mr-2">
                    <IconCirclePlus />
                  </span>
                  Add KPI Goal
                </Button>
              </div>

              <div className="h-[75%] overflow-y-auto">
                <KPITable
                  goals={kpiGoals}
                  kpiOptions={options}
                  branches={ALL_BRANCHES}
                  handleRemoveKPI={handleRemoveKPI}
                />
              </div>
              <div className="flex justify-end space-x-2 mt-4">
                <Button variant="outline" onClick={handleSheetClose}>
                  Cancel
                </Button>
                <Button type="submit" onClick={handleSaveKPIGoals}>
                  Save
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <div className="flex flex-col space-y-4">
          <DataTable
            data={goalsData}
            columns={kpiGoalPlanTableColumns}
            pagination
            loading={loading}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export default KPIGoalPlanPage
